import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

/**
 * Bump ONLY on a breaking change to a stored value's shape. Every slice shares
 * it, so a bump silently discards every table preference for every user.
 */
const SCHEMA_VERSION = 1;

const SLICES = [
  "columnVisibility",
  "columnOrder",
  "columnSizing",
  "sorting",
  "columnFilters",
] as const;
export type Slice = (typeof SLICES)[number];

/**
 * Identifies one table's stored preferences.
 *
 * `scope` exists because sort and filters are not like column order. A stored
 * column order holds COLUMN IDS, which the code declares and which mean the same
 * thing everywhere — so a flat `table:projects:columnOrder` key was safe. A
 * stored filter holds ENTITY IDS (a work-item state UUID, a user id, a
 * custom-field value), so carrying one workspace's filters into another renders
 * an empty table with chips pointing at rows that do not exist.
 *
 * It is a required field, not an optional one, so a new call site cannot forget
 * to make the decision. Pass the workspace slug for a workspace-scoped table,
 * `<workspaceSlug>/<projectId>` for a project-scoped one, and `null` only when
 * the stored value genuinely holds no entity ids.
 *
 * **Building a compound scope: guard each half BEFORE composing it.** `"" + "/"
 * + id` is a truthy string, so the empty-scope guard below cannot see through
 * one — a momentarily-unset route param silently writes to `@/<id>`, a bucket
 * nothing reads again. Pass `null` unless every part is present, e.g.
 * `slug && id ? `${slug}/${id}` : null`.
 */
export interface TablePrefsKey {
  /** Must match the `tableId` the surface passes to `<DataTable>`. */
  tableId: string;
  scope: string | null;
}

/** `table:<tableId>[@<scope>]:<slice>`. A null scope reproduces the pre-existing
 *  key exactly, so today's stored column order/visibility/sizing still loads.
 *
 *  An empty-string scope is NOT a synonym for `null` — it is a programming
 *  error. A caller that genuinely wants no scope passes `null` explicitly
 *  (that is why `scope` is required rather than optional). Left unguarded, a
 *  momentarily-empty route param would silently write to the global,
 *  unscoped bucket and collide with the pre-existing legacy key. */
export function tablePrefsStorageKey(key: TablePrefsKey, slice: Slice): string {
  if (key.scope === "") {
    console.error(
      `[table-prefs] empty-string scope for ${key.tableId}:${slice} — pass null for no scope, not ""`,
    );
  }
  const id = key.scope ? `${key.tableId}@${key.scope}` : key.tableId;
  return `table:${id}:${slice}`;
}

/**
 * Read one slice.
 *
 * `isValid` is optional because the three original slices never had it and
 * changing their behaviour is out of scope. Pass it for anything holding data
 * from outside the code — see the guards below.
 *
 * It takes a plain `boolean` predicate rather than a `value is T` type guard:
 * this function always returns via `value as T` regardless (the narrowing is
 * never used), so a type-predicate signature here is decorative, and callers
 * that only have a `boolean` predicate (see the `isValid` parameter of
 * `ensureEntry` in `use-table-preferences.ts`, which is forwarded here from a
 * ref) would otherwise need an unsound cast to satisfy it.
 */
export function readTablePref<T>(
  key: TablePrefsKey,
  slice: Slice,
  fallback: T,
  isValid?: (value: unknown) => boolean,
): T {
  // Once per logical read, not once per mention: building it is also what
  // reports an empty-string scope, so calling it again in each log line made
  // one bug log its `console.error` two or three times over.
  const storageKey = tablePrefsStorageKey(key, slice);
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw) as { _v?: number; value?: unknown };
    if (parsed?._v !== SCHEMA_VERSION) return fallback;
    // `?? fallback` and NOT a truthiness/length check: a stored `[]` is a user
    // who cleared the sort, which must not read as "no preference".
    const value = parsed.value ?? fallback;
    if (isValid && !isValid(value)) {
      // "unusable" rather than "malformed": since NG-629 a predicate can also
      // reject a perfectly well-formed value whose column ids no longer exist,
      // and a log that calls that malformed sends the reader looking for a JSON
      // bug instead of a renamed column.
      console.warn(`[table-prefs] discarding unusable ${slice} for ${storageKey}`, value);
      return fallback;
    }
    return value as T;
  } catch (err) {
    console.warn(`[table-prefs] could not read ${slice} for ${storageKey}:`, err);
    return fallback;
  }
}

export function writeTablePref<T>(key: TablePrefsKey, slice: Slice, value: T): void {
  // Hoisted for the same reason as in `readTablePref`.
  const storageKey = tablePrefsStorageKey(key, slice);
  try {
    localStorage.setItem(storageKey, JSON.stringify({ _v: SCHEMA_VERSION, value }));
  } catch (err) {
    // Private mode and quota exhaustion both throw here. Losing a preference is
    // not worth breaking the interaction that triggered the write, so this is a
    // warn rather than a rethrow — but it is never silent.
    console.warn(`[table-prefs] could not write ${slice} for ${storageKey}:`, err);
  }
}

export function isSortingState(value: unknown): value is SortingState {
  return (
    Array.isArray(value) &&
    value.every((entry) => {
      if (typeof entry !== "object" || entry === null) return false;
      const e = entry as { id?: unknown; desc?: unknown };
      return typeof e.id === "string" && typeof e.desc === "boolean";
    })
  );
}

/**
 * `isSortingState`, narrowed to the ids a surface can actually sort by (NG-629).
 *
 * Shape validation alone is not enough for this slice, and storage is why. A
 * sort set through the UI can only ever name a live sortable column, so the
 * setter path never needed an allow-list — but a PERSISTED sort outlives the
 * release that stored it. Rename or drop a column and the old id is still
 * sitting in localStorage, still `{ id: string, desc: boolean }`, and so still
 * passes `isSortingState`. What it then does, none of it visible:
 *
 * - It suppresses the surface's default sort, because a stored value that
 *   validates is preferred to the fallback.
 * - On a server-sorted surface it becomes an `order_by` token the API does not
 *   recognise, so the API quietly applies its own default ordering instead of
 *   the one the header claims.
 * - It cannot be cleared from the UI, because no header carries that id — and
 *   it still counts toward `sorting.length`, so the next real header click
 *   renders as the SECOND key of a multi-sort whose first key is invisible.
 *
 * Rejecting the array wholesale rather than filtering the bad entries out is
 * deliberate: a partially-restored multi-sort is a different sort from the one
 * the user saved, and falling back to the surface default is the honest answer.
 *
 * **`sortableIds` must be the ids of columns whose headers really sort** — on a
 * server-sorted surface, the intersection with the API's `order_by` allow-list.
 * Too narrow is not a safe default: it discards a preference the user set
 * legitimately, and does it silently on their next visit.
 */
export function isSortingStateOver(sortableIds: Iterable<string>): (value: unknown) => boolean {
  const allowed = new Set(sortableIds);
  return (value: unknown) => isSortingState(value) && value.every((entry) => allowed.has(entry.id));
}

/** Mirrors the `ColumnFilter` union in `column-filter.tsx`. All four shapes are
 *  JSON-safe (strings, string arrays, numbers, ISO date strings — no `Date`), so
 *  the round-trip is lossless; this guard is about untrusted input, not fidelity. */
function isColumnFilterValue(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const v = value as { type?: unknown; value?: unknown };
  if (v.type === "text") return typeof v.value === "string";
  if (v.type === "multi") {
    return Array.isArray(v.value) && v.value.every((entry) => typeof entry === "string");
  }
  if (v.type === "range") {
    const r = v.value as { min?: unknown; max?: unknown } | null;
    return (
      typeof r === "object" && r !== null && typeof r.min === "number" && typeof r.max === "number"
    );
  }
  if (v.type === "dateRange") {
    const d = v.value as { from?: unknown; to?: unknown } | null;
    return (
      typeof d === "object" && d !== null && typeof d.from === "string" && typeof d.to === "string"
    );
  }
  return false;
}

export function isColumnFiltersState(value: unknown): value is ColumnFiltersState {
  return (
    Array.isArray(value) &&
    value.every((filter) => {
      if (typeof filter !== "object" || filter === null) return false;
      const f = filter as { id?: unknown; value?: unknown };
      return typeof f.id === "string" && isColumnFilterValue(f.value);
    })
  );
}
